import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { todayYMD } from '../../utils/date';
import { addDays } from './plantafelViewport';
import { localDayKeyFromDate } from './mapRoutes';
import { UI_Z_CLASS } from '../../utils/uiZIndex';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONTHS = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

const PANEL_WIDTH = 280;
const PANEL_HEIGHT = 320;
const PANEL_GAP = 8;
const VIEWPORT_MARGIN = 12;

function placeCalendarPanel(rect: DOMRect): { left: number; top: number } {
  const left = Math.min(
    Math.max(rect.left + rect.width / 2 - PANEL_WIDTH / 2, VIEWPORT_MARGIN),
    window.innerWidth - PANEL_WIDTH - VIEWPORT_MARGIN,
  );

  const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
  const spaceAbove = rect.top - VIEWPORT_MARGIN;
  const opensAbove = spaceBelow < PANEL_HEIGHT + PANEL_GAP && spaceAbove > spaceBelow;
  const top = opensAbove
    ? Math.max(VIEWPORT_MARGIN, rect.top - PANEL_HEIGHT - PANEL_GAP)
    : rect.bottom + PANEL_GAP;

  return { left, top };
}

type MapDateNavigatorProps = {
  value: Date;
  onChange: (date: Date) => void;
  formatLabel: (date: Date) => string;
};

function ymdToLocalDate(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function MapDateNavigator({ value, onChange, formatLabel }: MapDateNavigatorProps) {
  const [open, setOpen] = React.useState(false);
  const [panelPosition, setPanelPosition] = React.useState<{ left: number; top: number } | null>(null);
  const dateButtonRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const valueYmd = localDayKeyFromDate(value);

  const [viewMonth, setViewMonth] = React.useState(() => new Date(value.getFullYear(), value.getMonth(), 1));

  const updatePanelPosition = React.useCallback(() => {
    const rect = dateButtonRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPanelPosition(placeCalendarPanel(rect));
  }, []);

  const toggleCalendar = () => {
    if (open) {
      setOpen(false);
      return;
    }
    const rect = dateButtonRef.current?.getBoundingClientRect();
    if (rect) setPanelPosition(placeCalendarPanel(rect));
    setOpen(true);
  };

  React.useEffect(() => {
    if (!open) return;
    setViewMonth(new Date(value.getFullYear(), value.getMonth(), 1));
  }, [open, value]);

  React.useLayoutEffect(() => {
    if (!open) return;
    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);
    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [open, updatePanelPosition]);

  React.useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (dateButtonRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days: (number | null)[] = Array(startOffset).fill(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const handleSelectDay = (ymd: string) => {
    onChange(ymdToLocalDate(ymd));
    setOpen(false);
  };

  const calendarPanel =
    open && panelPosition ? (
      <div
        ref={panelRef}
        className={`fixed ${UI_Z_CLASS.portalPanel} rounded-xl border border-app-border bg-app-surface p-3 shadow-lg`}
        style={{
          top: panelPosition.top,
          left: panelPosition.left,
          width: PANEL_WIDTH,
        }}
        role="dialog"
        aria-label="Datum wählen"
      >
        <div className="mb-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setViewMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1))}
            className="rounded-lg p-1.5 text-sm font-medium text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted"
            aria-label="Vorheriger Monat"
          >
            ‹
          </button>
          <span className="text-sm font-semibold text-app-text">
            {MONTHS[month]} {year}
          </span>
          <button
            type="button"
            onClick={() => setViewMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1))}
            className="rounded-lg p-1.5 text-sm font-medium text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted"
            aria-label="Nächster Monat"
          >
            ›
          </button>
        </div>
        <div className="grid grid-cols-7 gap-0.5 text-center">
          {WEEKDAYS.map((w) => (
            <div key={w} className="py-1 text-xs font-medium text-app-text-subtle">
              {w}
            </div>
          ))}
          {days.map((day, i) => {
            if (day === null) return <div key={`e-${i}`} />;
            const ymd = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isSelected = valueYmd === ymd;
            const isToday = ymd === todayYMD();
            return (
              <button
                key={ymd}
                type="button"
                onClick={() => handleSelectDay(ymd)}
                className={`h-8 w-8 rounded-lg text-sm font-medium transition-colors ${
                  isSelected
                    ? 'bg-pipedrive-blue text-white'
                    : 'text-app-text-muted hover:bg-app-hover'
                } ${isToday && !isSelected ? 'ring-1 ring-pipedrive-blue/50' : ''}`}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>
    ) : null;

  return (
    <>
      <div className="flex items-center overflow-hidden rounded-md border border-app-border bg-app-surface">
        <button
          type="button"
          className="inline-flex h-8 w-8 items-center justify-center border-r border-app-border text-app-text-subtle hover:bg-app-hover"
          onClick={() => onChange(addDays(value, -1))}
          aria-label="Vorheriger Tag"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <button
          ref={dateButtonRef}
          type="button"
          className="min-w-[11rem] px-3 py-1.5 text-center text-xs font-medium text-app-text hover:bg-app-hover"
          onClick={toggleCalendar}
          aria-expanded={open}
          aria-haspopup="dialog"
        >
          {formatLabel(value)}
        </button>

        <button
          type="button"
          className="inline-flex h-8 w-8 items-center justify-center border-l border-app-border text-app-text-subtle hover:bg-app-hover"
          onClick={() => onChange(addDays(value, 1))}
          aria-label="Nächster Tag"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {open && typeof document !== 'undefined' ? createPortal(calendarPanel, document.body) : null}
    </>
  );
}
